import React, { useEffect, useRef, useState } from 'react';
import {
    Drawer, Spin, Tag, Space, Button, Typography, Descriptions, Image,
    Modal, Input, Tooltip, message, Alert, Radio, DatePicker, Card, Empty,
} from 'antd';
import {
    CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined,
    QuestionCircleOutlined, FileImageOutlined, ExclamationCircleOutlined,
    CloudDownloadOutlined, SearchOutlined, SwapOutlined, SendOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { fuelFormImageApi } from './api';
import ToolTraceViewer from '../FlightLogRecognition/ToolTraceViewer';

const { Text } = Typography;

// 与列表页一致：统一收敛为 5 档展示；低置信度/多候选的细节由下方 Alert 提示
const STATUS_TAG = {
    pending_extract: { color: 'blue', label: '识别中' },
    pending_match: { color: 'blue', label: '识别中' },
    pending_review: { color: 'gold', label: '待审核' },
    pending_review_low_confidence: { color: 'gold', label: '待审核' },
    pending_review_ambiguous_match: { color: 'gold', label: '待审核' },
    failed_permanent: { color: 'red', label: '识别失败' },
    confirmed: { color: 'green', label: '已确认' },
    rejected: { color: 'default', label: '已拒绝' },
};

const MATCH_STATUS_TAG = {
    auto_matched: { color: 'green', label: '自动匹配' },
    manual_matched: { color: 'cyan', label: '人工选定' },
    unmatched: { color: 'default', label: '未匹配' },
};

const FIELD_LABELS = {
    out_fuel_kg: 'OUT 油量 (kg)',
    previous_fuel_kg: '加油前 (kg)',
    fuel_uplift_kg: '加油量 (kg)',
    fuel_uplift_liters: '加油量 (L)',
    fuel_density: '密度',
    off_fuel_kg: 'OFF 油量 (kg)',
    on_fuel_kg: 'ON 油量 (kg)',
    in_fuel_kg: 'IN 油量 (kg)',
    aircraft_reg: '机号',
    flight_number: '航班号',
    flight_date: '日期',
    station: '站点',
};

const FUEL_FIELDS = [
    'fuel_uplift_kg', 'fuel_uplift_liters', 'fuel_density',
    'previous_fuel_kg', 'out_fuel_kg', 'off_fuel_kg', 'on_fuel_kg', 'in_fuel_kg',
];

const HEADER_FIELDS = ['flight_number', 'flight_date', 'aircraft_reg', 'station'];

const ConfidenceTag = ({ value, low }) => {
    if (value == null) return <Tag>未知</Tag>;
    const pct = (value * 100).toFixed(0) + '%';
    return <Tag color={low ? 'orange' : value > 0.85 ? 'green' : 'gold'}>{pct}</Tag>;
};

const isLowConfidenceField = (perFieldConfidence, field) => {
    const v = perFieldConfidence?.[field];
    if (v == null) return false;
    return v < 0.7;
};

const flightLabel = (f) => {
    if (!f) return '-';
    const route = (f.departure_airport || f.arrival_airport)
        ? ` · ${f.departure_airport || '?'}→${f.arrival_airport || '?'}` : '';
    return `${f.flight_number || '-'} · ${f.flight_date || '-'} · ${f.aircraft_reg || '无机号'}${route}`;
};

/** 识别日期是 YYYY-MM-DD，按日历日解析，避免被当成 UTC 午夜再转成本地日期。 */
const parseFlightDate = (value) => {
    if (!value) return null;
    const dateOnly = String(value).match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
    if (!dateOnly) return null;
    const [year, month, day] = dateOnly.split('-').map(Number);
    const parsed = dayjs(new Date(year, month - 1, day));
    return parsed.isValid() ? parsed : null;
};

const searchQueryFromPrefill = (defaultQuery) => ({
    flight_number: defaultQuery?.flight_number || '',
    flight_date: parseFlightDate(defaultQuery?.flight_date),
    aircraft_reg: defaultQuery?.aircraft_reg || '',
});

const sameImageId = (a, b) => a != null && b != null && String(a) === String(b);

/** 候选 / 搜索结果的单选列表。 */
const FlightRadioList = ({ flights, value, onChange }) => (
    <Radio.Group
        value={value}
        onChange={(e) => {
            const id = e.target.value;
            onChange(id, flights.find((f) => f.flight_operation_id === id) || null);
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
    >
        {flights.map((f) => (
            <Radio key={f.flight_operation_id} value={f.flight_operation_id}>
                <Space size={6} wrap>
                    <Text style={{ fontFamily: 'Courier New, monospace' }}>{flightLabel(f)}</Text>
                    {f.status && <Tag style={{ margin: 0 }}>{f.status}</Tag>}
                </Space>
            </Radio>
        ))}
    </Radio.Group>
);

/**
 * 航班搜索区：按航班号 / 日期 / 机号搜 FOP，选中一条作为绑定目标。
 * 打开当前单时用识别基础信息预填一次；之后改搜索条件不跟识别字段逐字同步。
 */
const FlightSearchPanel = ({ defaultQuery, selectedId, onSelect }) => {
    const [query, setQuery] = useState(() => searchQueryFromPrefill(defaultQuery));
    const [results, setResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const searchSeq = useRef(0);

    const handleSearch = async () => {
        const params = {};
        if (query.flight_number.trim()) params.flight_number = query.flight_number.trim();
        if (query.flight_date) params.flight_date = query.flight_date.format('YYYY-MM-DD');
        if (query.aircraft_reg.trim()) params.aircraft_reg = query.aircraft_reg.trim();
        if (!Object.keys(params).length) {
            message.warning('请至少填一个搜索条件');
            return;
        }
        const seq = ++searchSeq.current;
        setSearching(true);
        try {
            const res = await fuelFormImageApi.searchFlights(params);
            if (seq !== searchSeq.current) return;
            setResults((res.data || res)?.items || []);
        } catch (err) {
            if (seq !== searchSeq.current) return;
            message.error('搜索失败: ' + (err.response?.data?.detail?.message || err.message));
        } finally {
            if (seq === searchSeq.current) setSearching(false);
        }
    };

    return (
        <div>
            <Space.Compact style={{ width: '100%' }}>
                <Input
                    size="small" placeholder="航班号" allowClear style={{ width: '28%' }}
                    value={query.flight_number} onPressEnter={handleSearch}
                    onChange={(e) => setQuery((q) => ({ ...q, flight_number: e.target.value }))}
                />
                <DatePicker
                    size="small" placeholder="航班日期(UTC)" style={{ width: '32%' }}
                    value={query.flight_date}
                    onChange={(v) => setQuery((q) => ({ ...q, flight_date: v }))}
                />
                <Input
                    size="small" placeholder="机号" allowClear style={{ width: '24%' }}
                    value={query.aircraft_reg} onPressEnter={handleSearch}
                    onChange={(e) => setQuery((q) => ({ ...q, aircraft_reg: e.target.value }))}
                />
                <Button size="small" type="primary" icon={<SearchOutlined />} loading={searching} onClick={handleSearch}>
                    搜索
                </Button>
            </Space.Compact>
            {results !== null && (
                <div style={{ marginTop: 8, maxHeight: 220, overflow: 'auto' }}>
                    {results.length ? (
                        <FlightRadioList flights={results} value={selectedId} onChange={onSelect} />
                    ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未找到航班，请调整条件" />
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * FuelFormImageReviewDrawer — 双栏审核 Drawer（加油单识别航班勾稽版）。
 *
 * - 左栏：图片预览（签名 URL）。
 * - 右栏：识别字段 + 航班核对（自动匹配结果 / 候选选择 / 手动搜索）
 *         + AI 查证过程 + 确认绑定 / 拒绝 / 改绑。
 */
const FuelFormImageReviewDrawer = ({ imageId, open, onClose, onChanged, hasEditPermission }) => {
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [signedUrl, setSignedUrl] = useState(null);
    const [fieldOverrides, setFieldOverrides] = useState({});
    const [selectedFlightId, setSelectedFlightId] = useState(null);
    const [selectedFlightInfo, setSelectedFlightInfo] = useState(null);
    const [rebindMode, setRebindMode] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const detailRequestSeq = useRef(0);

    const fetchDetail = async (requestedId) => {
        const targetId = requestedId || imageId;
        if (!targetId) return;
        const requestSeq = ++detailRequestSeq.current;
        setLoading(true);
        setSelectedFlightId(null);
        setSelectedFlightInfo(null);
        try {
            const [detailRes, urlRes] = await Promise.all([
                fuelFormImageApi.detail(targetId),
                fuelFormImageApi.signedUrl(targetId).catch(() => null),
            ]);
            if (requestSeq !== detailRequestSeq.current) return;
            const d = detailRes.data || detailRes;
            if (!sameImageId(d?.id, targetId)) return;
            setDetail(d);
            const u = urlRes?.data || urlRes;
            setSignedUrl(u?.signed_url || null);
            setFieldOverrides({});
            setSelectedFlightId(d.flight_operation_id || null);
            setSelectedFlightInfo(d.flight_operation || null);
            setRebindMode(false);
        } catch (err) {
            if (requestSeq !== detailRequestSeq.current) return;
            message.error('加载详情失败: ' + (err.response?.data?.detail?.message || err.message));
        } finally {
            if (requestSeq === detailRequestSeq.current) setLoading(false);
        }
    };

    useEffect(() => {
        if (!open || !imageId) return undefined;
        setDetail(null);
        setSignedUrl(null);
        setFieldOverrides({});
        setSelectedFlightId(null);
        setSelectedFlightInfo(null);
        setRebindMode(false);
        fetchDetail(imageId);
        return () => { detailRequestSeq.current += 1; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, imageId]);

    const handleSelectFlight = (id, info) => {
        setSelectedFlightId(id);
        if (info) setSelectedFlightInfo(info);
    };

    const handleConfirm = async () => {
        if (!detail || !sameImageId(detail.id, imageId)) return;
        if (!selectedFlightId) {
            message.warning('请先选择要绑定的航班');
            return;
        }
        setSubmitting(true);
        try {
            // 油量字段从 Input 出来是字符串，落库前转回数字（可转时）
            const overrides = Object.fromEntries(
                Object.entries(fieldOverrides).map(([k, v]) => {
                    if (FUEL_FIELDS.includes(k) && v !== '' && v != null && !Number.isNaN(Number(v))) {
                        return [k, Number(v)];
                    }
                    return [k, v];
                }),
            );
            const body = {
                flight_operation_id: selectedFlightId,
                field_overrides: Object.keys(overrides).length ? overrides : undefined,
            };
            const res = await fuelFormImageApi.confirm(detail.id, body);
            const out = res.data || res;
            const dup = out?.already_bound_image_ids || [];
            if (dup.length) {
                message.warning(`已确认绑定航班；注意该航班已有 ${dup.length} 张其他已确认加油单`);
            } else {
                message.success('已确认绑定航班');
            }
            onChanged?.();
            onClose?.();
        } catch (err) {
            message.error('确认失败: ' + (err.response?.data?.detail?.message || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    // 已确认记录改绑到另一个航班（S3.4）
    const handleRebind = async () => {
        if (!detail || !selectedFlightId) {
            message.warning('请先选择新航班');
            return;
        }
        if (selectedFlightId === detail.flight_operation_id) {
            message.warning('选择的航班与当前绑定相同');
            return;
        }
        setSubmitting(true);
        try {
            await fuelFormImageApi.rebindFlight(detail.id, selectedFlightId);
            message.success('已改绑航班');
            onChanged?.();
            await fetchDetail();
        } catch (err) {
            message.error('改绑失败: ' + (err.response?.data?.detail?.message || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = () => {
        let reason = '';
        Modal.confirm({
            title: '拒绝图片',
            icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
            content: (
                <Input.TextArea rows={3} placeholder="请输入拒绝原因（必填）" onChange={(e) => { reason = e.target.value; }} />
            ),
            okText: '确认拒绝', okButtonProps: { danger: true }, cancelText: '取消',
            onOk: async () => {
                if (!reason.trim()) {
                    message.warning('请输入拒绝原因');
                    return Promise.reject();
                }
                try {
                    await fuelFormImageApi.reject(detail.id, reason.trim());
                    message.success('已拒绝');
                    onChanged?.();
                    onClose?.();
                } catch (err) {
                    message.error('拒绝失败: ' + (err.response?.data?.detail?.message || err.message));
                }
            },
        });
    };

    const handleRecover = async () => {
        try {
            await fuelFormImageApi.recover(detail.id);
            message.success('已恢复审核');
            onChanged?.();
            await fetchDetail();
        } catch (err) {
            message.error('恢复失败: ' + (err.response?.data?.detail?.message || err.message));
        }
    };

    // T44: 重新识别 — 仅 failed_permanent 详情可用，需二次确认（消耗 VLM 配额）
    const handleReExtract = () => {
        Modal.confirm({
            title: '重新识别',
            icon: <ExclamationCircleOutlined style={{ color: '#1677ff' }} />,
            content: '将再次调用 AI 识别这张图片（消耗一次配额）。如果原因是图片本身模糊，建议先「拒绝」并重新上传更清晰的图片。',
            okText: '确定重新识别', cancelText: '取消',
            onOk: async () => {
                try {
                    await fuelFormImageApi.reExtract(detail.id);
                    message.success('已加入识别队列，请稍候刷新');
                    onChanged?.();
                    await fetchDetail();
                } catch (err) {
                    message.error('重新识别失败: ' + (err.response?.data?.detail?.message || err.message));
                }
            },
        });
    };

    const isCurrent = Boolean(detail && imageId && sameImageId(detail.id, imageId));
    const ef = (isCurrent && detail.extracted_fields) || {};
    const rawVlm = (isCurrent && detail.raw_vlm_response) || {};
    const perFieldConfidence = rawVlm._per_field_confidence;
    const toolTrace = rawVlm._tool_trace;

    const candidates = (isCurrent && detail.flight_match_candidates?.candidates) || [];
    const isConfirmed = isCurrent && detail.status === 'confirmed';

    const reviewable = hasEditPermission && isCurrent && [
        'pending_review', 'pending_review_low_confidence', 'pending_review_ambiguous_match',
    ].includes(detail.status);

    const canReject = hasEditPermission && isCurrent && [
        'pending_review', 'pending_review_low_confidence', 'pending_review_ambiguous_match', 'failed_permanent',
    ].includes(detail.status);

    const canRecover = hasEditPermission && isCurrent && detail.status === 'rejected';
    const canReExtract = hasEditPermission && isCurrent && detail.status === 'failed_permanent';

    const headerValue = (field) => fieldOverrides[field] ?? ef[field];

    const renderHeaderField = (field) => {
        const value = headerValue(field);
        const lowConf = isLowConfidenceField(perFieldConfidence, field);
        return (
            <Descriptions.Item label={FIELD_LABELS[field] || field} key={field}
                contentStyle={lowConf ? { background: '#fff7e6', padding: '4px 8px', borderRadius: 4 } : undefined}>
                {reviewable ? (
                    <Input
                        size="small"
                        value={value || ''}
                        onChange={(e) => setFieldOverrides({ ...fieldOverrides, [field]: e.target.value })}
                        suffix={lowConf ? <Tooltip title="该字段置信度偏低，请重点核对"><QuestionCircleOutlined style={{ color: '#fa8c16' }} /></Tooltip> : null}
                    />
                ) : (
                    <Text>{value || '-'}</Text>
                )}
            </Descriptions.Item>
        );
    };

    // 识别值 vs 绑定航班关键信息不一致时提示（机号 / 航班号 / 日期）
    const flightForCompare = selectedFlightInfo;
    const mismatches = [];
    if (flightForCompare && isCurrent) {
        const shownNumber = headerValue('flight_number');
        const shownDate = headerValue('flight_date');
        const shownReg = headerValue('aircraft_reg');
        if (shownNumber && flightForCompare.flight_number && shownNumber !== flightForCompare.flight_number) {
            mismatches.push(`航班号 ${shownNumber} ≠ ${flightForCompare.flight_number}`);
        }
        if (shownDate && flightForCompare.flight_date && shownDate !== flightForCompare.flight_date) {
            mismatches.push(`日期 ${shownDate} ≠ ${flightForCompare.flight_date}`);
        }
        if (shownReg && flightForCompare.aircraft_reg && shownReg !== flightForCompare.aircraft_reg) {
            mismatches.push(`机号 ${shownReg} ≠ ${flightForCompare.aircraft_reg}`);
        }
    }

    const tagInfo = STATUS_TAG[detail?.status] || { color: 'default', label: detail?.status };
    const matchTag = MATCH_STATUS_TAG[detail?.flight_match_status] || MATCH_STATUS_TAG.unmatched;

    const renderFlightCard = () => {
        if (!detail) return null;
        const boundFlight = detail.flight_operation;
        return (
            <Card
                size="small"
                style={{ marginBottom: 16 }}
                title={(
                    <Space size={6}>
                        <SendOutlined />
                        <span>航班核对</span>
                        <Tag color={matchTag.color} style={{ margin: 0 }}>{matchTag.label}</Tag>
                    </Space>
                )}
                extra={isConfirmed && hasEditPermission && (
                    <Button size="small" icon={<SwapOutlined />} onClick={() => {
                        setRebindMode((v) => !v);
                        setSelectedFlightId(detail.flight_operation_id || null);
                        setSelectedFlightInfo(detail.flight_operation || null);
                    }}>
                        {rebindMode ? '取消改绑' : '改绑航班'}
                    </Button>
                )}
            >
                {/* 当前绑定 / 自动匹配结果 */}
                {boundFlight ? (
                    <Alert
                        type={boundFlight.deleted ? 'error' : 'success'}
                        showIcon
                        style={{ marginBottom: 8 }}
                        message={(
                            <Space size={6} wrap>
                                <Text strong>{isConfirmed ? '已绑定：' : '建议绑定：'}</Text>
                                <Text style={{ fontFamily: 'Courier New, monospace' }}>{flightLabel(boundFlight)}</Text>
                                {boundFlight.status && <Tag style={{ margin: 0 }}>{boundFlight.status}</Tag>}
                                {boundFlight.deleted && <Tag color="red" style={{ margin: 0 }}>航班已删除</Tag>}
                            </Space>
                        )}
                    />
                ) : (
                    !candidates.length && (
                        <Alert
                            type="warning" showIcon style={{ marginBottom: 8 }}
                            message="未自动匹配到航班，请用下方搜索手动选定"
                        />
                    )
                )}

                {/* 选中目标与识别字段不一致提示（不阻断） */}
                {mismatches.length > 0 && (reviewable || rebindMode) && (
                    <Alert
                        type="warning" showIcon style={{ marginBottom: 8 }}
                        message={`选中航班与识别结果不一致：${mismatches.join('；')}`}
                    />
                )}

                {/* 多候选选择（识别后匹配歧义） */}
                {reviewable && candidates.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>
                            候选航班（{candidates.length}），请选择：
                        </Text>
                        <FlightRadioList
                            flights={candidates}
                            value={selectedFlightId}
                            onChange={handleSelectFlight}
                        />
                    </div>
                )}

                {/* 手动搜索：待审核态始终可用；已确认态进入改绑模式后可用 */}
                {(reviewable || (isConfirmed && rebindMode)) && (
                    <div>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                            手动搜索航班（候选不对 / 未匹配时使用）：
                        </Text>
                        <FlightSearchPanel
                            key={imageId}
                            defaultQuery={{
                                flight_number: headerValue('flight_number'),
                                flight_date: headerValue('flight_date'),
                                aircraft_reg: headerValue('aircraft_reg'),
                            }}
                            selectedId={selectedFlightId}
                            onSelect={handleSelectFlight}
                        />
                    </div>
                )}

                {/* 改绑提交 */}
                {isConfirmed && rebindMode && (
                    <div style={{ marginTop: 10, textAlign: 'right' }}>
                        <Button
                            type="primary" size="small" icon={<SwapOutlined />}
                            loading={submitting}
                            disabled={!selectedFlightId || selectedFlightId === detail.flight_operation_id}
                            onClick={handleRebind}
                        >
                            改绑到选中航班
                        </Button>
                    </div>
                )}
            </Card>
        );
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width={1180}
            destroyOnHidden
            title={
                <Space>
                    <FileImageOutlined />
                    <Text strong>{isCurrent ? (detail.file_name || '加油单图片审核') : '加油单图片审核'}</Text>
                    {isCurrent && <Tag color={tagInfo.color}>{tagInfo.label}</Tag>}
                    {isCurrent && (
                        <ConfidenceTag
                            value={detail.extraction_confidence}
                            low={detail.status === 'pending_review_low_confidence'}
                        />
                    )}
                </Space>
            }
            footer={
                <Space>
                    {canRecover && <Button icon={<ReloadOutlined />} onClick={handleRecover}>恢复审核</Button>}
                    {canReExtract && (
                        <Button type="primary" ghost icon={<ReloadOutlined />} onClick={handleReExtract}>
                            重新识别
                        </Button>
                    )}
                    {canReject && <Button danger icon={<CloseCircleOutlined />} onClick={handleReject}>拒绝</Button>}
                    {reviewable && (
                        <Tooltip title={selectedFlightId ? '' : '需要先选定一个航班'}>
                            <Button
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                loading={submitting}
                                disabled={!selectedFlightId}
                                onClick={handleConfirm}
                            >
                                确认绑定航班
                            </Button>
                        </Tooltip>
                    )}
                </Space>
            }
        >
            <Spin spinning={loading || (open && !!imageId && !isCurrent)}>
                {isCurrent && (
                    <div style={{ display: 'grid', gridTemplateColumns: '480px 1fr', gap: 24 }}>
                        {/* 左栏：图片预览 */}
                        <div>
                            {signedUrl ? (
                                <Image src={signedUrl} alt={detail.file_name}
                                    style={{ borderRadius: 6, maxHeight: 720, objectFit: 'contain' }} />
                            ) : (
                                <Alert type="warning" showIcon message="无法加载图片" />
                            )}
                            <div style={{ marginTop: 12, fontSize: 12, color: '#8c8c8c' }}>
                                {detail.file_name} · {(detail.file_size_bytes / 1024).toFixed(1)} KB
                            </div>
                        </div>

                        {/* 右栏：识别字段 + 航班核对 + AI 查证过程 */}
                        <div>
                            {detail.source_type === 'external_crawl' && (
                                <Alert
                                    style={{ marginBottom: 12 }}
                                    type="info"
                                    showIcon
                                    icon={<CloudDownloadOutlined />}
                                    message={
                                        detail.external_crawl_file?.deleted ? (
                                            <Space size={6}>
                                                <Text>来源：自动抓取</Text>
                                                <Text type="secondary">（原抓取记录已删除）</Text>
                                            </Space>
                                        ) : (
                                            <Space size={6} wrap>
                                                <Text>
                                                    来源：自动抓取
                                                    {detail.external_crawl_file
                                                        ? `（${detail.external_crawl_file.site_key || '-'} / ${detail.external_crawl_file.date_key || '-'}）`
                                                        : ''}
                                                </Text>
                                                {detail.external_crawl_file && (
                                                    <Link
                                                        to={`/settlement/external-crawl-config?config_id=${detail.external_crawl_file.url_config_id}&file_id=${detail.external_crawl_file.id}`}
                                                    >
                                                        查看抓取记录
                                                    </Link>
                                                )}
                                            </Space>
                                        )
                                    }
                                />
                            )}
                            {detail.status === 'failed_permanent' && (
                                <Alert
                                    style={{ marginBottom: 12 }}
                                    type="error"
                                    showIcon
                                    message="识别失败"
                                    description={
                                        (detail.raw_vlm_response?.error_message
                                            || '识别已重试 3 次仍未通过')
                                        + ' — 可点击「重新识别」让 AI 再试一次，或「拒绝」并重新上传更清晰的图片。'
                                    }
                                />
                            )}
                            {detail.status === 'pending_review_low_confidence' && (
                                <Alert style={{ marginBottom: 12 }} type="warning" showIcon
                                    message="整体置信度低于阈值或关键字段缺失，请仔细核对高亮字段" />
                            )}
                            {detail.status === 'pending_review_ambiguous_match' && (
                                <Alert style={{ marginBottom: 12 }} type="warning" showIcon
                                    message={`匹配到 ${candidates.length} 个候选航班，请在下方选择正确的航班`} />
                            )}

                            <Descriptions size="small" column={2} bordered title="识别基础信息" style={{ marginBottom: 16 }}>
                                {HEADER_FIELDS.map(renderHeaderField)}
                            </Descriptions>

                            {renderFlightCard()}

                            <Descriptions size="small" column={2} bordered title="识别油量字段" style={{ marginBottom: 16 }}>
                                {FUEL_FIELDS.map(renderHeaderField)}
                            </Descriptions>

                            {Array.isArray(toolTrace) && toolTrace.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <ToolTraceViewer trace={toolTrace} />
                                </div>
                            )}

                            {detail.review_notes && (
                                <Alert
                                    style={{ marginTop: 4 }}
                                    type="info"
                                    message="审核备注"
                                    description={detail.review_notes}
                                />
                            )}
                        </div>
                    </div>
                )}
            </Spin>
        </Drawer>
    );
};

export default FuelFormImageReviewDrawer;
